import { useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { IEditPrepItemNameVariables, editPrepItemName } from '../gql'

interface IEditPrepItemNameModalProps {
  visible: boolean
  onClose: () => void
  prepItemId: string
  prepItemName: string
  onSuccess?: () => void
}

const EditPrepItemNameModal = ({
  visible,
  onClose,
  prepItemId,
  prepItemName,
  onSuccess,
}: IEditPrepItemNameModalProps) => {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [editPrepItemNameMutation] =
    useMutation<IEditPrepItemNameVariables>(editPrepItemName)

  useEffect(() => {
    if (visible) {
      setName(prepItemName)
      setError(null)
      setSuccess(false)
    }
  }, [prepItemName, visible])

  const handleSave = async () => {
    if (!name || name.trim() === '') {
      setError('Prep Item Name is required')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await editPrepItemNameMutation({
        variables: {
          iPrepItemId: prepItemId,
          iName: name.trim(),
        },
      })

      setSuccess(true)

      if (onSuccess) {
        onSuccess()
      }

      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Prep Item Name</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Prep Item Key
            </Typography>
            <TextField
              value={prepItemId}
              disabled
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Prep Item Name
            </Typography>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter prep item name"
              fullWidth
              size="small"
              disabled={isLoading}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully updated
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}
          >
            <Button onClick={onClose} variant="outlined" disabled={isLoading}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={success || isLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EditPrepItemNameModal
